/**
 * Custom hook for managing aggregated timeline data
 * 
 * This hook provides access to timeline data from all sites,
 * handles loading states, caching, and provides filtering capabilities.
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  initialTimelineLoad, 
  getTimelineStats, 
  filterTimelineData 
} from '../services/timelineAggregationService';

export const useTimelineData = (autoLoad = true) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  /**
   * Loads timeline data with optional force refresh
   */
  const loadTimelines = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      const data = await initialTimelineLoad(forceRefresh);
      setTimelineData(data);
      setLastUpdated(new Date(data.lastUpdated));
      console.log('Timeline data loaded successfully');
    } catch (err) {
      console.error('Failed to load timeline data:', err);
      setError(err.message || 'Failed to load timeline data');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refreshes timeline data from source
   */
  const refreshTimelines = useCallback(() => {
    return loadTimelines(true);
  }, [loadTimelines]);

  /**
   * Gets timeline statistics
   */
  const getStats = useCallback(() => {
    return getTimelineStats(timelineData);
  }, [timelineData]);

  /**
   * Filters timeline data by criteria
   */
  const filterData = useCallback((filters) => {
    return filterTimelineData(timelineData, filters);
  }, [timelineData]);

  /**
   * Gets periods by era
   */
  const getPeriodsByEra = useCallback((era) => {
    if (!timelineData || !timelineData.periodsByEra) return [];
    return timelineData.periodsByEra[era] || [];
  }, [timelineData]);

  /**
   * Gets periods by culture
   */
  const getPeriodsByCulture = useCallback((culture) => {
    if (!timelineData || !timelineData.periodsByCulture) return [];
    return timelineData.periodsByCulture[culture] || [];
  }, [timelineData]);

  /**
   * Gets periods by region
   */
  const getPeriodsByRegion = useCallback((region) => {
    if (!timelineData || !timelineData.periodsByRegion) return [];
    return timelineData.periodsByRegion[region] || [];
  }, [timelineData]);

  /**
   * Gets all unique eras
   */
  const getAvailableEras = useCallback(() => {
    if (!timelineData || !timelineData.periodsByEra) return [];
    return Object.keys(timelineData.periodsByEra).sort();
  }, [timelineData]);

  /**
   * Gets all unique cultures
   */
  const getAvailableCultures = useCallback(() => {
    if (!timelineData || !timelineData.periodsByCulture) return [];
    return Object.keys(timelineData.periodsByCulture).sort();
  }, [timelineData]);

  /**
   * Gets all unique regions
   */
  const getAvailableRegions = useCallback(() => {
    if (!timelineData || !timelineData.periodsByRegion) return [];
    return Object.keys(timelineData.periodsByRegion).sort();
  }, [timelineData]);

  /**
   * Checks if cache is stale (older than specified hours)
   */
  const isCacheStale = useCallback((maxAgeHours = 24) => {
    if (!lastUpdated) return true;
    const ageHours = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60);
    return ageHours > maxAgeHours;
  }, [lastUpdated]);

  // Auto-load on mount if enabled
  useEffect(() => {
    if (autoLoad) {
      loadTimelines();
    }
  }, [autoLoad, loadTimelines]);

  return {
    // Data
    timelineData,
    loading,
    error,
    lastUpdated,
    
    // Actions
    loadTimelines,
    refreshTimelines,
    
    // Computed data
    getStats,
    filterData,
    getPeriodsByEra,
    getPeriodsByCulture,
    getPeriodsByRegion,
    getAvailableEras,
    getAvailableCultures,
    getAvailableRegions,
    
    // Utilities
    isCacheStale,
    
    // Convenience getters
    isLoaded: !!timelineData,
    hasError: !!error,
    isEmpty: timelineData && timelineData.allPeriods.length === 0
  };
};